
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'product' | 'service';
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  locale?: string;
  siteName?: string;
  canonical?: string;
  noindex?: boolean;
  nofollow?: boolean;
}

const DEFAULT_SEO = {
  title: 'Médicace - Plateforme de Santé Intelligente | Suivi Médical & Téléconsultation',
  description: 'Médicace est votre plateforme de santé digitale complète. Gérez vos rendez-vous médicaux, suivez vos traitements, consultez des professionnels de santé et accédez à l\'IA médicale. Votre santé, simplifiée.',
  keywords: 'santé digitale, téléconsultation, suivi médical, rendez-vous médecin, pharmacie en ligne, IA médicale, carnet de santé numérique, rappels médicaments, tracking santé, professionnels santé',
  image: '/lovable-uploads/8003781b-4d38-497c-8862-00124549a126.png',
  siteName: 'Médicace',
  locale: 'fr_FR',
  type: 'website' as const,
  author: 'Équipe Médicace'
};

export const SEOHead: React.FC<SEOProps> = ({
  title,
  description,
  keywords,
  image,
  url,
  type = 'website',
  author,
  publishedTime,
  modifiedTime,
  locale = 'fr_FR',
  siteName,
  canonical,
  noindex = false,
  nofollow = false
}) => {
  const location = useLocation();
  
  const seoData = {
    title: title ? `${title} | ${DEFAULT_SEO.siteName}` : DEFAULT_SEO.title,
    description: description || DEFAULT_SEO.description,
    keywords: keywords || DEFAULT_SEO.keywords,
    image: image || DEFAULT_SEO.image,
    url: url || `${window.location.origin}${location.pathname}`,
    type,
    author: author || DEFAULT_SEO.author,
    siteName: siteName || DEFAULT_SEO.siteName,
    locale,
    canonical: canonical || `${window.location.origin}${location.pathname}`
  };

  useEffect(() => {
    // Update document title
    document.title = seoData.title;

    // Function to update or create meta tags
    const updateMetaTag = (property: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attribute}="${property}"]`) as HTMLMetaElement;
      
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, property);
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    // Basic meta tags
    updateMetaTag('description', seoData.description);
    updateMetaTag('keywords', seoData.keywords);
    updateMetaTag('author', seoData.author);
    updateMetaTag('viewport', 'width=device-width, initial-scale=1.0');
    updateMetaTag('theme-color', '#3b82f6');

    // Robots meta
    const robotsContent = [];
    if (noindex) robotsContent.push('noindex');
    if (nofollow) robotsContent.push('nofollow');
    if (robotsContent.length === 0) robotsContent.push('index', 'follow');
    updateMetaTag('robots', robotsContent.join(', '));

    // Open Graph meta tags
    updateMetaTag('og:title', seoData.title, true);
    updateMetaTag('og:description', seoData.description, true);
    updateMetaTag('og:image', seoData.image, true);
    updateMetaTag('og:url', seoData.url, true);
    updateMetaTag('og:type', seoData.type, true);
    updateMetaTag('og:site_name', seoData.siteName, true);
    updateMetaTag('og:locale', seoData.locale, true);

    // Twitter Card meta tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', seoData.title);
    updateMetaTag('twitter:description', seoData.description);
    updateMetaTag('twitter:image', seoData.image);

    // Article specific meta tags
    if (type === 'article') {
      if (publishedTime) updateMetaTag('article:published_time', publishedTime, true);
      if (modifiedTime) updateMetaTag('article:modified_time', modifiedTime, true);
      updateMetaTag('article:author', seoData.author, true);
    }

    // Canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = seoData.canonical;

    // Add hreflang for French
    let hreflangLink = document.querySelector('link[rel="alternate"][hreflang="fr"]') as HTMLLinkElement;
    if (!hreflangLink) {
      hreflangLink = document.createElement('link');
      hreflangLink.rel = 'alternate';
      hreflangLink.hreflang = 'fr';
      document.head.appendChild(hreflangLink);
    }
    hreflangLink.href = seoData.url;

  }, [seoData, type, publishedTime, modifiedTime, noindex, nofollow]);

  return null;
};
